import React from 'react';
import { observer } from 'mobx-react-lite';
import { Util } from 'utils/common.utils';
import { _noop } from 'utils/function.utils';
import { CardThemeType } from './card-type';
import styles from './card.module.scss';

export type CardProps = {
  children: JSX.Element;
  centered?: boolean;
  classes?: string[];
  backgroundType?: CardThemeType;
  backgroundImage?: string;
  backgroundColor?: string;
  smallBackgroundImage?: boolean;
  backgroundOptions?: {
    grayscale?: boolean;
    overlay?: string;
    additionalOptions?: {
      backgroundPosition?: string;
      backgroundAttachment?: string;
      backgroundSize?: string;
    };
  };
  fullHeight?: boolean;
  fullWidth?: boolean;
  allowOverflow?: boolean;
  mobilePadding?: boolean;
  noMargin?: boolean;
  noPadding?: boolean;
  addOnPadding?: boolean;
  useBlur?: boolean;
  innerClasses?: string[];
  onClick?: (e: React.MouseEvent) => void;
  analyticsComponent?: any;
  useInnerDivForChildren?: boolean;
};

const getBGStyle = (backgroundType?: CardThemeType) => {
  switch (backgroundType) {
    case CardThemeType.Dark:
      return styles.darkCardBackground;
    case CardThemeType.Light:
      return styles.lightCardBackground;
    case CardThemeType.Glass:
      return styles.glassCardBackground;
    case CardThemeType.GlassSecondary:
      return styles.glassSecondaryCardBackground;
    case CardThemeType.Dashed:
      return styles.dashedGradientCardBackground;
    case CardThemeType.Primary:
      return styles.primaryBlueBackground;
    case CardThemeType.DarkGlass:
      return styles.darkGlassCardBackground;
    case CardThemeType.DarkGlow:
      return styles.darkGlowCardBackground;
    case CardThemeType.Grey:
      return styles.GreyCardBackground;
    case CardThemeType.GreyGradient:
      return styles.GreyGradientBackground;
    case CardThemeType.Black:
      return styles.BlackCardBackground;
    case CardThemeType.PatternGlass:
      return styles.glassPatternCardBackground;
    case CardThemeType.LightShadow:
      return styles.LightShadowBackground;
    case CardThemeType.LightBorder:
      return styles.lightBorder;
    default:
      return styles.transparent;
  }
};

export const Card = observer(
  ({
    backgroundType,
    backgroundImage,
    backgroundColor,
    backgroundOptions,
    centered,
    classes,
    children,
    smallBackgroundImage = false,
    fullHeight,
    fullWidth,
    allowOverflow = false,
    mobilePadding = false,
    noMargin = false,
    noPadding = false,
    addOnPadding = false,
    useBlur = false,
    innerClasses,
    useInnerDivForChildren = true,
    onClick = _noop,
    analyticsComponent,
  }: CardProps) => {
    const bkgOverlay = backgroundOptions?.overlay ? backgroundOptions?.overlay + ', ' : '';
    const background = {
      background: `${bkgOverlay}url(${backgroundImage}) ${backgroundOptions?.additionalOptions?.backgroundPosition || 'center center'
        } / ${backgroundOptions?.additionalOptions?.backgroundSize
          ? backgroundOptions?.additionalOptions?.backgroundSize
          : smallBackgroundImage
            ? '120%'
            : 'cover'
        } no-repeat ${backgroundColor ? backgroundColor : ''}`, // FIXME: 120% background-size is a workaround for the current images provided
    };

    const classNames = [
      styles.cardContainer,

      getBGStyle(backgroundType),

      useBlur ? styles.useBackgroundBlur : '',
      centered ? styles.centered : '',
      mobilePadding ? styles.reducedPaddingOnMobile : '',
      !noMargin ? styles.hasMargin : '',
      noPadding ? styles.noPadding : '',
      addOnPadding ? styles.addOnCardPadding : '',

      ...(classes ? classes : []),

      fullHeight ? styles.fullHeight : '',
      fullWidth ? styles.fullWidth : '',
    ];

    const cssClassName = Util.GetCssClassName(classNames);

    const innerClassNames = [
      fullHeight ? styles.fullHeight : '',
      ...(innerClasses?.length ? innerClasses : [])
    ];

    const innerCssClassName = Util.GetCssClassName(innerClassNames);

    return (
      <div
        style={{
          ...(backgroundType !== CardThemeType.DarkGlass && backgroundImage
            ? background
            : backgroundColor
              ? { backgroundColor: backgroundColor }
              : {}),
          ...(onClick !== _noop ? { cursor: 'pointer' } : {}),
          ...(allowOverflow ? { overflow: 'visible' } : {}),
          ...(backgroundOptions?.grayscale ? { filter: 'grayscale(1)' } : {}),
        }}
        className={cssClassName}
        onClick={(e) => onClick(e)}
        {...(analyticsComponent || '')}
      >
        {(backgroundType === CardThemeType.DarkGlass || backgroundType === CardThemeType.PatternGlass) && (
          <>
            <div
              className={styles.overlay}
              style={{
                ...(backgroundImage ? background : backgroundColor ? { backgroundColor: backgroundColor } : {}),
              }}
            ></div>
            <div
              className={styles.underlay}
              style={{
                ...(backgroundType === CardThemeType.PatternGlass
                  ? {
                    background: `url(${Util.GetImageAsset('background-pattern-mobile.png')}) top center`,
                  }
                  : {}),
              }}
            ></div>
          </>
        )}
        {useInnerDivForChildren && <div className={innerCssClassName}>
          {children}
        </div>}
        {!useInnerDivForChildren && children}
      </div>
    );
  }
);